<template>
  <div>
    <dashboard-header :text="$t('role.viewRole')" :on-back="goBack"/>

    <div class="d-flex align-content-center justify-content-center">
      <v-card
          elevation="0"
          :loading="loading"
          class="card-border-10 mx-16 w-75">
        <div class="pa-5">
          <!-- Role Information -->
          <v-card class="card-border-10 mb-4" outlined>
            <div class="custom-card-head d-flex p-3 text-18px">{{ $t('role.roleInformation') }}</div>
            <v-divider class="ma-0"></v-divider>
            
            <div class="pa-4">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="mb-3">
                    <strong>{{ $t('role.name') }}:</strong> {{ roleDetail?.name || '-' }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-3">
                    <strong>{{ $t('role.type') }}:</strong> 
                    <v-chip 
                      :color="roleDetail?.isDefault ? 'warning' : 'success'"
                      text-color="white"
                      x-small
                    >
                      {{ roleDetail?.isDefault ? $t('role.system') : $t('role.custom') }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="mb-3">
                    <strong>{{ $t('role.description') }}:</strong> {{ roleDetail?.description || '-' }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-3">
                    <strong>{{ $t('role.status') }}:</strong> 
                    <v-chip 
                      :color="roleDetail?.active ? 'success' : 'error'"
                      text-color="white"
                      x-small
                    >
                      {{ roleDetail?.active ? $t('role.active') : $t('role.inactive') }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-3">
                    <strong>{{ $t('common.createdAt') }}:</strong> {{ formatDate(roleDetail?.createdAt) }}
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card>

          <!-- Permissions Section -->
          <v-card class="card-border-10 mb-4" outlined>
            <div class="custom-card-head d-flex p-3 text-18px">
              {{ $t('role.permissions') }}
              <v-chip small color="primary" class="ml-2">
                {{ permissionList.length }}
              </v-chip>
            </div>
            <v-divider class="ma-0"></v-divider>
            
            <div class="pa-4">
              <v-expansion-panels v-if="groupedPermissions.length > 0">
                <v-expansion-panel
                  v-for="(group, index) in groupedPermissions"
                  :key="index"
                >
                  <v-expansion-panel-header>
                    <span class="font-weight-medium">
                      {{ group.name }}
                      <v-chip x-small class="ml-2">
                        {{ group.permissions.length }}
                      </v-chip>
                    </span>
                  </v-expansion-panel-header>
                  
                  <v-expansion-panel-content>
                    <v-list dense>
                      <v-list-item
                        v-for="permission in group.permissions"
                        :key="permission.code"
                      >
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-check</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ permission.action || permission.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ permission.code }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
              
              <v-alert v-else type="info" outlined>
                {{ $t('role.noPermissions') }}
              </v-alert>
            </div>
          </v-card>

          <!-- Users Section -->
          <v-card class="card-border-10" outlined>
            <div class="custom-card-head d-flex p-3 text-18px">
              {{ $t('role.users') }}
              <v-chip small color="info" class="ml-2">
                {{ users.length }}
              </v-chip>
            </div>
            <v-divider class="ma-0"></v-divider>
            
            <div class="pa-4">
              <v-data-table
                v-if="users.length > 0"
                :headers="userHeaders"
                :items="users"
                :loading="loadingUsers"
                :items-per-page="10"
                dense
              >
                <template v-slot:item.name="{ item }">
                  <span class="font-weight-medium">{{ getUserName(item) }}</span>
                </template>
                
                <template v-slot:item.email="{ item }">
                  <span class="text--secondary">{{ getUserEmail(item) }}</span>
                </template>
                
                <template v-slot:item.type="{ item }">
                  <v-chip x-small>
                    {{ item.userType === 'ACCOUNT' ? $t('role.account') : $t('role.employee') }}
                  </v-chip>
                </template>
              </v-data-table>
              
              <v-alert v-else type="info" outlined>
                {{ $t('role.noUsers') }}
              </v-alert>
            </div>
          </v-card>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import { get } from '@/api'
import { API2 } from '@/api'
import mixin from "../../mixin";
import DashboardHeader from "../component/DashboardHeader";
import moment from 'moment'

export default {
  components: {DashboardHeader},
  mixins: [mixin],
  props: {
    openTab: {
      type: Function,
      required: true
    },
    item: {
      type: Object,
    },
  },
  data() {
    return {
      loading: false,
      loadingUsers: false,
      roleDetail: null,
      users: []
    };
  },
  computed: {
    permissionList() {
      return this.roleDetail && this.roleDetail.permissions ? this.roleDetail.permissions : []
    },
    userHeaders() {
      return [
        {
          text: this.$t('common.name'),
          value: 'name'
        },
        {
          text: this.$t('common.email'),
          value: 'email'
        },
        {
          text: this.$t('role.userType'),
          value: 'type'
        }
      ]
    },
    groupedPermissions() {
      const groups = {}
      
      this.permissionList.forEach(permission => {
        const module = permission.module || 'Other'
        if (!groups[module]) {
          groups[module] = {
            name: module,
            permissions: []
          }
        }
        groups[module].permissions.push(permission)
      })
      
      return Object.values(groups)
    }
  },
  methods: {
    onTab(item) {
      this.roleDetail = item
      if (item) {
        this.loadRoleDetails()
      }
    },
    
    async loadRoleDetails() {
      this.loading = true
      try {
        // Load complete role details with permissions
        const response = await get(this, `${API2.ROLE}/${this.roleDetail.id}`)
        if (response && response.data && (response.data.code === 0 || response.data.status === 200)) {
          this.roleDetail = response.data.data
        }
        
        // Load users
        await this.loadUsers()
      } catch (error) {
        this.showToast('error', this.$t('common.loadError'))
      } finally {
        this.loading = false
      }
    },
    
    async loadUsers() {
      this.loadingUsers = true
      try {
        const response = await get(this, `${API2.ROLE}/${this.roleDetail.id}/users`)
        if (response && response.data && (response.data.code === 0 || response.data.status === 200)) {
          const data = response.data.data
          this.users = Array.isArray(data) ? data : []
        }
      } catch (error) {
        this.users = []
      } finally {
        this.loadingUsers = false
      }
    },
    
    getUserName(user) {
      if (user.userType === 'ACCOUNT') {
        return user.name || '-'
      } else {
        // For employees with titleId 10 (khoa accounts), use displayName if available
        if (user.titleId === 10 && user.displayName) {
          return user.displayName
        }
        return user.name || '-'
      }
    },
    
    getUserEmail(user) {
      if (user.userType === 'ACCOUNT') {
        return user.email || '-'
      } else {
        return user.email || '-'
      }
    },
    
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY HH:mm') : '-'
    },
    
    goBack() {
      this.openTab(0)
    },
  },
}
</script>

<style scoped>
</style>